import { useState, useEffect, useMemo, useCallback } from 'react';
import { logger } from '@/lib/logger';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronRight, ChevronDown, Package, AlertCircle, Loader2, Check } from 'lucide-react';
import { CategoryDropdown, type CategorySelection } from './CategoryDropdown';
import { useMergedCategoriesHierarchy } from '@/features/product-library/hooks/useMergedCategories';
import { createPortalioClient } from '@/services/portalio/client';
import { formatCurrency } from '@/utils/formatting';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { useFloorPlanContext } from '../FloorPlanProvider';

interface PortalProduct {
  id: string;
  name: string;
  description?: string;
  brand?: string;
  model?: string;
  partNumber?: string;
  quantity: number;
  unitPrice: number;
  sellPrice: number;
  totalPrice: number;
  category?: string;
  imageUrl?: string;
  specifications?: any;
  // Portal-specific
  referenceId?: string;
  area?: string;
  option?: string;
}

interface PortalProposal {
  id: number;
  proposalName: string;
  proposalNumber: number;
  customerName: string;
  totalAmount: number;
  areas: Array<{
    areaName: string;
    options: Array<{
      optionName: string;
      items: PortalProduct[];
    }>;
  }>;
}

interface ProductSelection {
  productId: string;
  category: CategorySelection | null;
  referenceId?: string;
  isAutoApplied?: boolean;
}

interface PortalProductSelectorProps {
  organizationId: string;
  proposalId: number;
  proposalName: string;
  customerName: string;
  totalAmount: number;
  placedProductIds?: string[];
  onBack: () => void;
  onImport: (selections: ProductSelection[]) => Promise<void>;
}

export function PortalProductSelector({
  organizationId,
  proposalId,
  proposalName,
  customerName,
  totalAmount,
  placedProductIds = [],
  onBack,
  onImport,
}: PortalProductSelectorProps) {
  const { data: floorPlanData } = useFloorPlanContext();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [proposal, setProposal] = useState<PortalProposal | null>(null);
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());
  const [expandedOptions, setExpandedOptions] = useState<Set<string>>(new Set());
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [categoryMappings, setCategoryMappings] = useState<Map<string, CategorySelection>>(
    new Map(),
  );
  const [autoApplyEnabled, setAutoApplyEnabled] = useState(true);
  const [autoAppliedProducts, setAutoAppliedProducts] = useState<Set<string>>(new Set());
  const [alreadyImportedProducts, setAlreadyImportedProducts] = useState<Set<string>>(new Set());

  // Fetch category templates to display proper names
  const { hierarchy: lightingHierarchy } = useMergedCategoriesHierarchy('lighting_fixtures');
  const { hierarchy: lvHierarchy } = useMergedCategoriesHierarchy('lv_products');

  // Helper to get display name for a category/subcategory
  const getCategoryDisplayName = useCallback(
    (category: string, subcategory?: string): string => {
      const allParents = [...lightingHierarchy.parents, ...lvHierarchy.parents];
      const allChildren = new Map([...lightingHierarchy.children, ...lvHierarchy.children]);

      if (subcategory) {
        // Look for subcategory template
        const children = allChildren.get(category) || [];
        const subTemplate = children.find(c => c.subcategory === subcategory);
        if (subTemplate) return subTemplate.display_name;
        // Fallback: format the raw subcategory
        return subcategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }

      // Look for parent category template
      const parentTemplate = allParents.find(p => p.category === category);
      if (parentTemplate) return parentTemplate.display_name;
      // Fallback: format the raw category
      return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    [lightingHierarchy, lvHierarchy],
  );

  // Load proposal details
  useEffect(() => {
    loadProposalDetails();
  }, [proposalId, organizationId]);

  const loadProposalDetails = async () => {
    if (!organizationId || !proposalId) return;

    try {
      setLoading(true);
      const client = createPortalioClient(organizationId);
      const detailResult = await client.getProposalDetails(proposalId);

      logger.log('Proposal details:', detailResult);
      setProposal(detailResult);

      // Load already imported products for this floor plan
      await loadAlreadyImportedProducts();

      // Load existing category mappings from product_library
      await loadExistingCategoryMappings(detailResult);

      // Auto-expand all areas by default
      if (detailResult.areas?.length > 0) {
        // Expand all areas
        const allAreaNames = detailResult.areas.map(area => area.areaName);
        setExpandedAreas(new Set(allAreaNames));

        // Optionally also expand all options within each area
        // Uncomment the following if you want all options expanded too:
        // const allOptions = detailResult.areas.flatMap(area =>
        //   area.options.map(option => `${area.areaName}-${option.optionName}`)
        // );
        // setExpandedOptions(new Set(allOptions));
      }
    } catch (error) {
      logger.error('Error loading proposal details:', error);
      toast.error('Failed to load proposal details');
    } finally {
      setLoading(false);
    }
  };

  const loadAlreadyImportedProducts = async () => {
    if (!organizationId || !floorPlanData?.floorPlan?.id) return;

    try {
      const supabase = await getAuthenticatedSupabaseClient();
      // Query portal_imported_products to find already imported items
      const { data, error } = await supabase
        .from('portal_imported_products')
        .select('portal_product_id')
        .eq('clerk_org_id', organizationId)
        .eq('floor_plan_id', floorPlanData.floorPlan.id);

      if (error) {
        logger.error('Error loading imported products:', error);
        return;
      }

      if (data) {
        const importedIds = new Set(data.map(item => item.portal_product_id));
        setAlreadyImportedProducts(importedIds);
        logger.log(`Found ${importedIds.size} already imported products`);
      }
    } catch (error) {
      logger.error('Error loading imported products:', error);
    }
  };

  const loadExistingCategoryMappings = async (proposalData: PortalProposal) => {
    if (!organizationId || !proposalData) return;

    try {
      const supabase = await getAuthenticatedSupabaseClient();
      // Collect all unique reference IDs and Portal product IDs from the proposal
      const referenceIds = new Set<string>();
      const portalProductIds = new Set<string>();

      proposalData.areas?.forEach(area => {
        area.options?.forEach(option => {
          option.items?.forEach(item => {
            if (item.referenceId) {
              referenceIds.add(String(item.referenceId));
            }
            portalProductIds.add(String(item.id));
          });
        });
      });

      // Skip if no IDs to search for
      if (portalProductIds.size === 0 && referenceIds.size === 0) {
        return;
      }

      // Build query with proper validation
      // Note: product_library uses portal_item_identifier and referenced_item_id
      let query = supabase
        .from('product_library')
        .select('portal_item_identifier, referenced_item_id, category, subcategory, portal_item_id')
        .eq('organization_id', organizationId)
        .eq('source', 'portal');

      // Build OR clause only with non-empty sets
      const orClauses = [];
      if (portalProductIds.size > 0) {
        // portal_product_id maps to portal_item_identifier in product_library
        const ids = Array.from(portalProductIds)
          .map(id => String(id)) // Convert to string first
          .filter(id => id && id.trim() !== '');
        if (ids.length > 0) {
          orClauses.push(`portal_item_identifier.in.(${ids.join(',')})`);
        }
      }
      if (referenceIds.size > 0) {
        // reference_id maps to referenced_item_id in product_library
        const ids = Array.from(referenceIds)
          .map(id => String(id)) // Convert to string first
          .filter(id => id && id.trim() !== '')
          .map(id => parseInt(id, 10))
          .filter(id => !isNaN(id));
        if (ids.length > 0) {
          orClauses.push(`referenced_item_id.in.(${ids.join(',')})`);
        }
      }

      // Only add OR clause if we have conditions
      if (orClauses.length > 0) {
        query = query.or(orClauses.join(','));
      }

      const { data: existingMappings, error } = await query;

      if (error) {
        logger.error('Error loading existing category mappings:', error);
        return;
      }

      if (existingMappings && existingMappings.length > 0) {
        const mappings = new Map<string, CategorySelection>();

        // Create maps for category and subcategory by reference_id and portal_id
        const mappingsByRef = new Map<string, { category: string; subcategory?: string }>();
        const mappingsByPortalId = new Map<string, { category: string; subcategory?: string }>();

        existingMappings.forEach(mapping => {
          const categoryData = {
            category: mapping.category,
            subcategory: mapping.subcategory || undefined,
          };

          // Map referenced_item_id (integer) to category/subcategory
          if (mapping.referenced_item_id) {
            mappingsByRef.set(String(mapping.referenced_item_id), categoryData);
          }
          // Map portal_item_identifier (string) to category/subcategory
          if (mapping.portal_item_identifier) {
            mappingsByPortalId.set(mapping.portal_item_identifier, categoryData);
          }
        });

        // Apply mappings to all products in the proposal
        proposalData.areas?.forEach(area => {
          area.options?.forEach(option => {
            option.items?.forEach(item => {
              // First try to match by reference_id, then by portal_product_id
              const existingMapping =
                (item.referenceId && mappingsByRef.get(item.referenceId)) ||
                mappingsByPortalId.get(item.id);

              if (existingMapping) {
                mappings.set(item.id, existingMapping);
              }
            });
          });
        });

        if (mappings.size > 0) {
          setCategoryMappings(mappings);
          logger.log(`Loaded ${mappings.size} existing category mappings`);
          toast.success(`Applied ${mappings.size} saved category mappings`);
        }
      }
    } catch (error) {
      logger.error('Error loading existing category mappings:', error);
    }
  };

  // Handle category change with auto-apply to matching reference IDs
  // Also auto-select the product when a category is assigned
  const handleCategoryChange = useCallback(
    (productId: string, category: CategorySelection | null, referenceId?: string) => {
      setCategoryMappings(prev => {
        const newMap = new Map(prev);
        if (category) {
          newMap.set(productId, category);
        } else {
          newMap.delete(productId);
        }
        return newMap;
      });

      // Auto-select the product when a category is assigned
      if (category) {
        setSelectedProducts(prev => {
          const newSet = new Set(prev);
          newSet.add(productId);
          return newSet;
        });
      }

      // Auto-apply to products with same reference ID
      if (autoApplyEnabled && referenceId && category) {
        const productsToAutoApply: string[] = [];

        proposal?.areas.forEach(area => {
          area.options?.forEach(option => {
            option.items?.forEach(item => {
              if (item.referenceId === referenceId && item.id !== productId) {
                productsToAutoApply.push(item.id);
              }
            });
          });
        });

        if (productsToAutoApply.length > 0) {
          setCategoryMappings(prev => {
            const newMap = new Map(prev);
            productsToAutoApply.forEach(id => {
              newMap.set(id, category);
            });
            return newMap;
          });

          // Also auto-select all auto-applied products
          setSelectedProducts(prev => {
            const newSet = new Set(prev);
            productsToAutoApply.forEach(id => newSet.add(id));
            return newSet;
          });

          setAutoAppliedProducts(prev => {
            const newSet = new Set(prev);
            productsToAutoApply.forEach(id => newSet.add(id));
            // Remove the auto-applied flag after a delay
            setTimeout(() => {
              setAutoAppliedProducts(current => {
                const updated = new Set(current);
                productsToAutoApply.forEach(id => updated.delete(id));
                return updated;
              });
            }, 2000);
            return newSet;
          });

          toast.success(`Category applied to ${productsToAutoApply.length + 1} matching products`);
        }
      }
    },
    [autoApplyEnabled, proposal],
  );

  // Toggle area expansion
  const toggleArea = (areaName: string) => {
    setExpandedAreas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(areaName)) {
        newSet.delete(areaName);
      } else {
        newSet.add(areaName);
      }
      return newSet;
    });
  };

  // Toggle option expansion
  const toggleOption = (key: string) => {
    setExpandedOptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Toggle product selection
  const toggleProduct = (productId: string) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  // Get all selectable products (exclude already imported)
  const getAllSelectableProducts = useCallback((): PortalProduct[] => {
    const products: PortalProduct[] = [];
    proposal?.areas.forEach(area => {
      area.options?.forEach(option => {
        option.items?.forEach(item => {
          if (!alreadyImportedProducts.has(item.id)) {
            products.push(item);
          }
        });
      });
    });
    return products;
  }, [proposal, alreadyImportedProducts]);

  // Get all selectable products in an area
  const getAreaSelectableProducts = useCallback(
    (areaName: string): PortalProduct[] => {
      const products: PortalProduct[] = [];
      const area = proposal?.areas.find(a => a.areaName === areaName);
      area?.options?.forEach(option => {
        option.items?.forEach(item => {
          if (!alreadyImportedProducts.has(item.id)) {
            products.push(item);
          }
        });
      });
      return products;
    },
    [proposal, alreadyImportedProducts],
  );

  // Select/deselect all products globally
  const toggleSelectAll = useCallback(() => {
    const selectableProducts = getAllSelectableProducts();
    const allSelected = selectableProducts.every(item => selectedProducts.has(item.id));

    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      selectableProducts.forEach(item => {
        if (allSelected) {
          newSet.delete(item.id);
        } else {
          newSet.add(item.id);
        }
      });
      return newSet;
    });
  }, [getAllSelectableProducts, selectedProducts]);

  // Select/deselect all products in an area
  const toggleSelectAllInArea = useCallback(
    (areaName: string) => {
      const areaProducts = getAreaSelectableProducts(areaName);
      const allSelected = areaProducts.every(item => selectedProducts.has(item.id));

      setSelectedProducts(prev => {
        const newSet = new Set(prev);
        areaProducts.forEach(item => {
          if (allSelected) {
            newSet.delete(item.id);
          } else {
            newSet.add(item.id);
          }
        });
        return newSet;
      });
    },
    [getAreaSelectableProducts, selectedProducts],
  );

  // Select all products in an option
  const toggleAllInOption = (area: string, option: string, items: PortalProduct[]) => {
    const selectableItems = items.filter(item => !alreadyImportedProducts.has(item.id));
    const allSelected = selectableItems.every(item => selectedProducts.has(item.id));

    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      selectableItems.forEach(item => {
        if (allSelected) {
          newSet.delete(item.id);
        } else {
          newSet.add(item.id);
        }
      });
      return newSet;
    });
  };

  // Calculate selected count
  const totalProductsCount = useMemo(() => {
    let count = 0;
    proposal?.areas.forEach(area => {
      area.options?.forEach(option => {
        count += option.items?.length || 0;
      });
    });
    return count;
  }, [proposal]);

  // Calculate selectable count (excluding already imported)
  const selectableProductsCount = useMemo(() => {
    return getAllSelectableProducts().length;
  }, [getAllSelectableProducts]);

  // Check if all selectable products are selected
  const allSelectableSelected = useMemo(() => {
    const selectableProducts = getAllSelectableProducts();
    return (
      selectableProducts.length > 0 && selectableProducts.every(p => selectedProducts.has(p.id))
    );
  }, [getAllSelectableProducts, selectedProducts]);

  // Check if some selectable products are selected
  const someSelectableSelected = useMemo(() => {
    const selectableProducts = getAllSelectableProducts();
    return selectableProducts.some(p => selectedProducts.has(p.id)) && !allSelectableSelected;
  }, [getAllSelectableProducts, selectedProducts, allSelectableSelected]);

  // Check selection state for an area
  const getAreaSelectionState = useCallback(
    (areaName: string) => {
      const areaProducts = getAreaSelectableProducts(areaName);
      const allSelected =
        areaProducts.length > 0 && areaProducts.every(p => selectedProducts.has(p.id));
      const someSelected = areaProducts.some(p => selectedProducts.has(p.id)) && !allSelected;
      return { allSelected, someSelected, count: areaProducts.length };
    },
    [getAreaSelectableProducts, selectedProducts],
  );

  // Handle import
  const handleImport = async () => {
    if (selectedProducts.size === 0) {
      toast.error('Please select at least one product to import');
      return;
    }

    try {
      setImporting(true);

      // Build selections with categories
      const selections: ProductSelection[] = Array.from(selectedProducts).map(productId => ({
        productId,
        category: categoryMappings.get(productId) || null,
        referenceId: findProductReferenceId(productId),
      }));

      await onImport(selections);
    } catch (error) {
      logger.error('Import error:', error);
      toast.error('Failed to import products');
    } finally {
      setImporting(false);
    }
  };

  // Helper to find product's reference ID
  const findProductReferenceId = (productId: string): string | undefined => {
    for (const area of proposal?.areas || []) {
      for (const option of area.options || []) {
        const product = option.items?.find(item => item.id === productId);
        if (product) return product.referenceId;
      }
    }
    return undefined;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-3" />
        <p className="text-muted-foreground">Failed to load proposal details</p>
        <Button variant="outline" onClick={onBack} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b bg-muted/30 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-base">
              Proposal #{proposal.proposalNumber} - {proposalName}
            </h3>
            <p className="text-sm text-muted-foreground">
              {customerName} • {formatCurrency(totalAmount)}
            </p>
          </div>
          {selectableProductsCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSelectAll}
              className="h-8 text-sm font-medium"
            >
              {allSelectableSelected ? 'Deselect All' : 'Select All'}
              <span className="ml-1.5 text-muted-foreground">
                ({selectedProducts.size}/{selectableProductsCount})
              </span>
            </Button>
          )}
        </div>
      </div>

      {/* Products List */}
      <ScrollArea className="flex-1 overflow-auto">
        <div className="p-4 space-y-4">
          {proposal.areas.map(area => {
            const areaSelectionState = getAreaSelectionState(area.areaName);
            return (
              <div key={area.areaName} className="border rounded-lg overflow-hidden shadow-sm mb-3">
                <Collapsible
                  open={expandedAreas.has(area.areaName)}
                  onOpenChange={() => toggleArea(area.areaName)}
                >
                  <div className="flex items-center justify-between w-full p-3 bg-muted/30 hover:bg-muted/50 transition-colors border-b">
                    <CollapsibleTrigger className="flex items-center gap-2 flex-1">
                      {expandedAreas.has(area.areaName) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <span className="font-semibold text-base">{area.areaName}</span>
                    </CollapsibleTrigger>
                    <div className="flex items-center gap-2">
                      {areaSelectionState.count > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            toggleSelectAllInArea(area.areaName);
                          }}
                          className="h-7 text-xs"
                        >
                          {areaSelectionState.allSelected ? 'Deselect' : 'Select All'}
                        </Button>
                      )}
                      <Badge variant="outline">
                        {area.options?.reduce((sum, opt) => sum + (opt.items?.length || 0), 0)}{' '}
                        items
                      </Badge>
                    </div>
                  </div>

                  <CollapsibleContent>
                    <div className="border-t">
                      {area.options?.map(option => {
                        const optionKey = `${area.areaName}-${option.optionName}`;
                        const allSelected = option.items?.every(item =>
                          selectedProducts.has(item.id),
                        );
                        const someSelected = option.items?.some(item =>
                          selectedProducts.has(item.id),
                        );
                        const hasMultipleOptions = (area.options?.length || 0) > 1;

                        // If only one option in the area, show products directly
                        if (!hasMultipleOptions) {
                          return (
                            <div key={optionKey} className="p-3 space-y-3 bg-background">
                              {option.items?.map(item => {
                                const isAlreadyImported = alreadyImportedProducts.has(item.id);
                                // For split items, check if any split variant is placed (e.g., "itemId:keypad", "itemId:dimmer")
                                const isOnFloorPlan =
                                  placedProductIds.includes(item.id) ||
                                  placedProductIds.some(id => id.startsWith(`${item.id}:`));

                                return (
                                  <div
                                    key={item.id}
                                    className={cn(
                                      'flex items-start gap-3 p-3 bg-background rounded-md border',
                                      'hover:border-primary/50 transition-all',
                                      autoAppliedProducts.has(item.id) &&
                                        'ring-2 ring-primary/30 bg-primary/5',
                                      isAlreadyImported && 'opacity-60',
                                    )}
                                  >
                                    <Checkbox
                                      checked={selectedProducts.has(item.id) || isAlreadyImported}
                                      onCheckedChange={() =>
                                        !isAlreadyImported && toggleProduct(item.id)
                                      }
                                      disabled={isAlreadyImported}
                                      className="mt-1"
                                    />

                                    <div className="flex-1 min-w-0 space-y-2">
                                      <div>
                                        {/* Line 1: Brand • Model */}
                                        {(item.brand || item.model) && (
                                          <p className="text-sm">
                                            {[item.brand, item.model].filter(Boolean).join(' • ')}
                                          </p>
                                        )}
                                        {/* Line 2: Description (using name or description field) */}
                                        {(item.description || item.name) && (
                                          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                                            {item.description || item.name}
                                          </p>
                                        )}
                                      </div>

                                      {/* Line 3: Qty and Ref */}
                                      <div className="flex items-center gap-3 flex-wrap">
                                        {isAlreadyImported && (
                                          <Badge
                                            variant="secondary"
                                            className="text-xs bg-green-100 text-green-700"
                                          >
                                            <Check className="h-3 w-3 mr-1" />
                                            Already Imported
                                          </Badge>
                                        )}
                                        {isOnFloorPlan && (
                                          <Badge
                                            variant="secondary"
                                            className="text-xs bg-blue-100 text-blue-700"
                                          >
                                            On Floor Plan
                                          </Badge>
                                        )}
                                        <span className="text-xs text-muted-foreground">
                                          Qty {item.quantity || 1}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="flex-shrink-0 w-48">
                                      <CategoryDropdown
                                        value={categoryMappings.get(item.id) || null}
                                        onChange={category =>
                                          handleCategoryChange(item.id, category, item.referenceId)
                                        }
                                        isAutoApplied={autoAppliedProducts.has(item.id)}
                                        className="w-full"
                                        placeholder="Select Category"
                                      />
                                      {(autoAppliedProducts.has(item.id) ||
                                        categoryMappings.get(item.id)) && (
                                        <div className="text-xs mt-1 flex items-center gap-1 flex-wrap">
                                          {autoAppliedProducts.has(item.id) && (
                                            <span className="text-primary flex items-center gap-1">
                                              <Check className="h-3 w-3" />
                                              Auto-applied
                                            </span>
                                          )}
                                          {categoryMappings.get(item.id) && (
                                            <span className="text-xs text-muted-foreground">
                                              {categoryMappings.get(item.id)?.subcategory
                                                ? `${getCategoryDisplayName(categoryMappings.get(item.id)!.category)} › ${getCategoryDisplayName(categoryMappings.get(item.id)!.category, categoryMappings.get(item.id)!.subcategory)}`
                                                : getCategoryDisplayName(
                                                    categoryMappings.get(item.id)!.category,
                                                  )}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }

                        // Multiple options - show collapsible
                        return (
                          <Collapsible
                            key={optionKey}
                            open={expandedOptions.has(optionKey)}
                            onOpenChange={() => toggleOption(optionKey)}
                          >
                            <div className="border-b last:border-b-0">
                              <div className="flex items-center justify-between w-full">
                                <CollapsibleTrigger className="flex items-center gap-2 flex-1 p-3 pl-8 hover:bg-accent/50 transition-colors">
                                  {expandedOptions.has(optionKey) ? (
                                    <ChevronDown className="h-3 w-3" />
                                  ) : (
                                    <ChevronRight className="h-3 w-3" />
                                  )}
                                  <Package className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-sm font-medium">{option.optionName}</span>
                                </CollapsibleTrigger>
                                <div className="flex items-center gap-2 pr-3">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={e => {
                                      e.stopPropagation();
                                      toggleAllInOption(
                                        area.areaName,
                                        option.optionName,
                                        option.items || [],
                                      );
                                    }}
                                    className="h-7 text-xs"
                                  >
                                    {allSelected
                                      ? 'Deselect All'
                                      : someSelected
                                        ? 'Select All'
                                        : 'Select All'}
                                  </Button>
                                  <Badge variant="outline" className="text-xs">
                                    {option.items?.length} items
                                  </Badge>
                                </div>
                              </div>

                              <CollapsibleContent>
                                <div className="p-3 pl-12 space-y-3 bg-muted/20">
                                  {option.items?.map(item => {
                                    const isAlreadyImported = alreadyImportedProducts.has(item.id);
                                    // For split items, check if any split variant is placed (e.g., "itemId:keypad", "itemId:dimmer")
                                    const isOnFloorPlan =
                                      placedProductIds.includes(item.id) ||
                                      placedProductIds.some(id => id.startsWith(`${item.id}:`));
                                    return (
                                      <div
                                        key={item.id}
                                        className={cn(
                                          'flex items-start gap-3 p-3 bg-background rounded-md border',
                                          'hover:border-primary/50 transition-all',
                                          autoAppliedProducts.has(item.id) &&
                                            'ring-2 ring-primary/30 bg-primary/5',
                                          isAlreadyImported && 'opacity-60',
                                        )}
                                      >
                                        <Checkbox
                                          checked={
                                            selectedProducts.has(item.id) || isAlreadyImported
                                          }
                                          onCheckedChange={() =>
                                            !isAlreadyImported && toggleProduct(item.id)
                                          }
                                          disabled={isAlreadyImported}
                                          className="mt-1"
                                        />

                                        <div className="flex-1 space-y-2">
                                          <div>
                                            {/* Line 1: Brand • Model */}
                                            {(item.brand || item.model) && (
                                              <p className="text-sm">
                                                {[item.brand, item.model]
                                                  .filter(Boolean)
                                                  .join(' • ')}
                                              </p>
                                            )}
                                            {/* Line 2: Description (using name or description field) */}
                                            {(item.description || item.name) && (
                                              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                                                {item.description || item.name}
                                              </p>
                                            )}
                                          </div>

                                          {/* Line 3: Qty and Ref */}
                                          <div className="flex items-center gap-3 flex-wrap">
                                            {isAlreadyImported && (
                                              <Badge
                                                variant="secondary"
                                                className="text-xs bg-green-100 text-green-700"
                                              >
                                                <Check className="h-3 w-3 mr-1" />
                                                Already Imported
                                              </Badge>
                                            )}
                                            {isOnFloorPlan && (
                                              <Badge
                                                variant="secondary"
                                                className="text-xs bg-blue-100 text-blue-700"
                                              >
                                                On Floor Plan
                                              </Badge>
                                            )}
                                            <span className="text-xs text-muted-foreground">
                                              Qty {item.quantity || 1}
                                            </span>
                                          </div>
                                        </div>

                                        <div className="w-48">
                                          <CategoryDropdown
                                            value={categoryMappings.get(item.id) || null}
                                            onChange={category =>
                                              handleCategoryChange(
                                                item.id,
                                                category,
                                                item.referenceId,
                                              )
                                            }
                                            isAutoApplied={autoAppliedProducts.has(item.id)}
                                            disabled={
                                              !selectedProducts.has(item.id) || isAlreadyImported
                                            }
                                            placeholder="Select Category"
                                          />
                                          {(autoAppliedProducts.has(item.id) ||
                                            categoryMappings.get(item.id)) && (
                                            <div className="text-xs mt-1 flex items-center gap-1 flex-wrap">
                                              {autoAppliedProducts.has(item.id) && (
                                                <span className="text-primary flex items-center gap-1">
                                                  <Check className="h-3 w-3" />
                                                  Auto
                                                </span>
                                              )}
                                              {categoryMappings.get(item.id) && (
                                                <span className="text-xs text-muted-foreground">
                                                  {categoryMappings.get(item.id)?.subcategory
                                                    ? `${getCategoryDisplayName(categoryMappings.get(item.id)!.category)} › ${getCategoryDisplayName(categoryMappings.get(item.id)!.category, categoryMappings.get(item.id)!.subcategory)}`
                                                    : getCategoryDisplayName(
                                                        categoryMappings.get(item.id)!.category,
                                                      )}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </CollapsibleContent>
                            </div>
                          </Collapsible>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t bg-background">
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={autoApplyEnabled}
                onCheckedChange={checked => setAutoApplyEnabled(checked as boolean)}
              />
              <span className="text-sm">Auto-assign category to matching Reference IDs</span>
            </label>

            <p className="text-sm text-muted-foreground">
              {selectedProducts.size} of {totalProductsCount} products selected
            </p>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onBack} disabled={importing} className="flex-1">
              Back
            </Button>
            <Button
              onClick={handleImport}
              disabled={importing || selectedProducts.size === 0}
              className="flex-1"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Importing...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Import Selected Products
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
